import { TimingTowerData, ProductionAssumptionsData } from '../types/widget';

/**
 * Day 0 Demo Data - Original 12-Day Schedule
 * 
 * Represents the initial state before production begins:
 * - 12 scheduled days (original schedule before revisions)
 * - All days in "planned" status
 * - No actual pages shot (all 0)
 * - Shows original schedule that will be revised during production
 * 
 * This demonstrates how the Timing Tower changes as the schedule is revised
 * to bring drift back to 0 (the goal of production management).
 */
export const demoDay0TimingTowerData: TimingTowerData = {
  currentScriptVersion: 'Strip Schedule',
  scriptColor: 'WHITE',
  scriptColorHex: '#FFFFFF',
  totalProductionDays: 12,
  totalScriptPages: 62.875, // Sum of planned pages for Days 1-12
  cumulativePagesShot: 0,
  totalScriptScenes: 75, // Total scenes planned for Days 1-12
  cumulativeScenesShot: 0,
  currentDayNumber: 1, // Day 0 demo starts on Day 1
  days: [
    {
      dayNumber: 1,
      status: 'planned',
      statusColor: '#A0AEC0',
      scenes: ['4', '51', '52', '58', '60pt'],
      scenesPlanned: ['4', '51', '52', '58', '60pt'],
      scenesShot: [],
      plannedScenesCount: 5,
      shotScenesCount: 0,
      plannedPages: '4 2/8',
      actualPages: '0/8',
      plannedPagesNumeric: 4.25,
      actualPagesNumeric: 0,
      progressPercent: 0,
      crewCallTime: '07:00',
      actualFirstShotTime: 'N/A',
      characters: [
        { characterId: '1', characterName: 'LAURA KALE', actorName: 'Cote de Pablo', callTime: '07:00', actualTime: null, arrivalVarianceMinutes: null },
        { characterId: '7', characterName: 'ETHAN KALE', actorName: 'JR Bourne', callTime: '07:00', actualTime: null, arrivalVarianceMinutes: null },
        { characterId: '8', characterName: 'KATHERINE (7yrs old)', actorName: 'Stella Edwards (K)', callTime: '07:00', actualTime: null, arrivalVarianceMinutes: null },
      ],
      punctualitySummary: { onTimeCount: 0, lateCount: 0, avgVarianceMinutes: null },
      firstLocation: { name: "LAURA'S HOUSE", status: 'EXT' },
      weather: { forecast: 'Mostly Sunny, 20% chance of rain', temp: 'High 66F / Low 39F' },
      lastUpdatedBy: { documentType: 'call_sheet', label: 'Original Schedule', reportedOn: null, updatedAt: null },
    },
    {
      dayNumber: 2,
      status: 'planned',
      statusColor: '#A0AEC0',
      scenes: ['14', '32A', '5A', '7A', '7B'],
      scenesPlanned: ['14', '32A', '5A', '7A', '7B'],
      scenesShot: [],
      plannedScenesCount: 5,
      shotScenesCount: 0,
      plannedPages: '4 5/8',
      actualPages: '0/8',
      plannedPagesNumeric: 4.625,
      actualPagesNumeric: 0,
      progressPercent: 0,
      crewCallTime: '07:00',
      actualFirstShotTime: 'N/A',
      characters: [
        { characterId: '1', characterName: 'LAURA KALE', actorName: 'Cote de Pablo', callTime: '09:00', actualTime: null, arrivalVarianceMinutes: null },
        { characterId: '3', characterName: 'DONNA NEURING', actorName: 'Annabelle Dexter-Jones', callTime: '16:00', actualTime: null, arrivalVarianceMinutes: null },
        { characterId: '4', characterName: 'FORDE', actorName: 'Toby Hemingway', callTime: '16:00', actualTime: null, arrivalVarianceMinutes: null },
      ],
      punctualitySummary: { onTimeCount: 0, lateCount: 0, avgVarianceMinutes: null },
      firstLocation: { name: 'PUMP STATION AERATION BASIN', status: 'EXT' },
      weather: { forecast: 'AM Clouds/PM Sun, 10% chance of rain', temp: 'High 66 / Low 39' },
      lastUpdatedBy: { documentType: 'call_sheet', label: 'Original Schedule', reportedOn: null, updatedAt: null },
    },
    {
      dayNumber: 3,
      status: 'planned',
      statusColor: '#A0AEC0',
      scenes: ['13', '16', '16A', '16PT', '55', '59', '60pt'],
      scenesPlanned: ['13', '16', '16A', '16PT', '55', '59', '60pt'],
      scenesShot: [],
      plannedScenesCount: 7,
      shotScenesCount: 0,
      plannedPages: '4 4/8',
      actualPages: '0/8',
      plannedPagesNumeric: 4.5,
      actualPagesNumeric: 0,
      progressPercent: 0,
      crewCallTime: '07:00',
      actualFirstShotTime: 'N/A',
      characters: [
        { characterId: '1', characterName: 'LAURA KALE', actorName: 'Cote de Pablo', callTime: '07:00', actualTime: null, arrivalVarianceMinutes: null },
        { characterId: '2', characterName: 'HAROLD KALE', actorName: 'Enrique Murciano', callTime: '09:00', actualTime: null, arrivalVarianceMinutes: null },
      ],
      punctualitySummary: { onTimeCount: 0, lateCount: 0, avgVarianceMinutes: null },
      firstLocation: { name: 'NEURING OFFICE', status: 'INT' },
      weather: { forecast: 'Mostly Sunny', temp: 'High 68 / Low 42' },
      lastUpdatedBy: { documentType: 'call_sheet', label: 'Original Schedule', reportedOn: null, updatedAt: null },
    },
    {
      dayNumber: 4,
      status: 'planned',
      statusColor: '#A0AEC0',
      scenes: ['26', '56', '57', '60pt', '60VFX', '7'],
      scenesPlanned: ['26', '56', '57', '60pt', '60VFX', '7'],
      scenesShot: [],
      plannedScenesCount: 6,
      shotScenesCount: 0,
      plannedPages: '5',
      actualPages: '0/8',
      plannedPagesNumeric: 5.0,
      actualPagesNumeric: 0,
      progressPercent: 0,
      crewCallTime: '07:00',
      actualFirstShotTime: 'N/A',
      characters: [
        { characterId: '1', characterName: 'LAURA KALE', actorName: 'Cote de Pablo', callTime: '07:00', actualTime: null, arrivalVarianceMinutes: null },
        { characterId: '5', characterName: 'MARGOT BISHOP', actorName: 'Ali Larter', callTime: '09:00', actualTime: null, arrivalVarianceMinutes: null },
      ],
      punctualitySummary: { onTimeCount: 0, lateCount: 0, avgVarianceMinutes: null },
      firstLocation: { name: 'MOTEL ROOM', status: 'INT' },
      weather: { forecast: 'Sunny', temp: 'High 70 / Low 45' },
      lastUpdatedBy: { documentType: 'call_sheet', label: 'Original Schedule', reportedOn: null, updatedAt: null },
    },
    {
      dayNumber: 5,
      status: 'planned',
      statusColor: '#A0AEC0',
      scenes: ['10', '11', '17', '31pt', '43', '44'],
      scenesPlanned: ['10', '11', '17', '31pt', '43', '44'],
      scenesShot: [],
      plannedScenesCount: 6,
      shotScenesCount: 0,
      plannedPages: '3 7/8',
      actualPages: '0/8',
      plannedPagesNumeric: 3.875,
      actualPagesNumeric: 0,
      progressPercent: 0,
      crewCallTime: '07:00',
      actualFirstShotTime: 'N/A',
      characters: [
        { characterId: '1', characterName: 'LAURA KALE', actorName: 'Cote de Pablo', callTime: '07:00', actualTime: null, arrivalVarianceMinutes: null },
        { characterId: '6', characterName: 'DALE HEWES', actorName: 'David James Elliot', callTime: '08:00', actualTime: null, arrivalVarianceMinutes: null },
      ],
      punctualitySummary: { onTimeCount: 0, lateCount: 0, avgVarianceMinutes: null },
      firstLocation: { name: 'HEWES RANCH', status: 'EXT' },
      weather: { forecast: 'Partly Cloudy', temp: 'High 65 / Low 40' },
      lastUpdatedBy: { documentType: 'call_sheet', label: 'Original Schedule', reportedOn: null, updatedAt: null },
    },
    {
      dayNumber: 6,
      status: 'planned',
      statusColor: '#A0AEC0',
      scenes: ['27', '28', '30', '40', '42'],
      scenesPlanned: ['27', '28', '30', '40', '42'],
      scenesShot: [],
      plannedScenesCount: 5,
      shotScenesCount: 0,
      plannedPages: '3 6/8',
      actualPages: '0/8',
      plannedPagesNumeric: 3.75,
      actualPagesNumeric: 0,
      progressPercent: 0,
      crewCallTime: '07:00',
      actualFirstShotTime: 'N/A',
      characters: [
        { characterId: '1', characterName: 'LAURA KALE', actorName: 'Cote de Pablo', callTime: '07:00', actualTime: null, arrivalVarianceMinutes: null },
        { characterId: '5', characterName: 'MARGOT BISHOP', actorName: 'Ali Larter', callTime: '07:00', actualTime: null, arrivalVarianceMinutes: null },
      ],
      punctualitySummary: { onTimeCount: 0, lateCount: 0, avgVarianceMinutes: null },
      firstLocation: { name: 'MOTEL EXTERIOR', status: 'EXT' },
      weather: { forecast: 'Sunny', temp: 'High 72 / Low 48' },
      lastUpdatedBy: { documentType: 'call_sheet', label: 'Original Schedule', reportedOn: null, updatedAt: null },
    },
    {
      dayNumber: 7,
      status: 'planned',
      statusColor: '#A0AEC0',
      scenes: ['25', '26pt', '26VFX', '30', '40pt', '41'],
      scenesPlanned: ['25', '26pt', '26VFX', '30', '40pt', '41'],
      scenesShot: [],
      plannedScenesCount: 6,
      shotScenesCount: 0,
      plannedPages: '6 5/8',
      actualPages: '0/8',
      plannedPagesNumeric: 6.625,
      actualPagesNumeric: 0,
      progressPercent: 0,
      crewCallTime: '07:00',
      actualFirstShotTime: 'N/A',
      characters: [
        { characterId: '1', characterName: 'LAURA KALE', actorName: 'Cote de Pablo', callTime: '07:00', actualTime: null, arrivalVarianceMinutes: null },
        { characterId: '4', characterName: 'FORDE', actorName: 'Toby Hemingway', callTime: '08:00', actualTime: null, arrivalVarianceMinutes: null },
      ],
      punctualitySummary: { onTimeCount: 0, lateCount: 0, avgVarianceMinutes: null },
      firstLocation: { name: 'DESERT HIGHWAY', status: 'EXT' },
      weather: { forecast: 'Clear', temp: 'High 75 / Low 50' },
      lastUpdatedBy: { documentType: 'call_sheet', label: 'Original Schedule', reportedOn: null, updatedAt: null },
    },
    {
      dayNumber: 8,
      status: 'planned',
      statusColor: '#A0AEC0',
      scenes: ['19', '20', '23', '24A', '42A', 'VFX1'],
      scenesPlanned: ['19', '20', '23', '24A', '42A', 'VFX1'],
      scenesShot: [],
      plannedScenesCount: 6,
      shotScenesCount: 0,
      plannedPages: '5 3/8',
      actualPages: '0/8',
      plannedPagesNumeric: 5.375,
      actualPagesNumeric: 0,
      progressPercent: 0,
      crewCallTime: '07:00',
      actualFirstShotTime: 'N/A',
      characters: [
        { characterId: '1', characterName: 'LAURA KALE', actorName: 'Cote de Pablo', callTime: '07:00', actualTime: null, arrivalVarianceMinutes: null },
        { characterId: '2', characterName: 'HAROLD KALE', actorName: 'Enrique Murciano', callTime: '07:00', actualTime: null, arrivalVarianceMinutes: null },
      ],
      punctualitySummary: { onTimeCount: 0, lateCount: 0, avgVarianceMinutes: null },
      firstLocation: { name: 'HOSPITAL', status: 'INT' },
      weather: { forecast: 'Sunny', temp: 'High 70 / Low 45' },
      lastUpdatedBy: { documentType: 'call_sheet', label: 'Original Schedule', reportedOn: null, updatedAt: null },
    },
    {
      dayNumber: 9,
      status: 'planned',
      statusColor: '#A0AEC0',
      scenes: ['20', '21', '24A', '32', '42A', '45', '47', '47A', '48', '48pt', '49', '54', 'VFX3'],
      scenesPlanned: ['20', '21', '24A', '32', '42A', '45', '47', '47A', '48', '48pt', '49', '54', 'VFX3'],
      scenesShot: [],
      plannedScenesCount: 13,
      shotScenesCount: 0,
      plannedPages: '5 4/8',
      actualPages: '0/8',
      plannedPagesNumeric: 5.5,
      actualPagesNumeric: 0,
      progressPercent: 0,
      crewCallTime: '07:00',
      actualFirstShotTime: 'N/A',
      characters: [
        { characterId: '1', characterName: 'LAURA KALE', actorName: 'Cote de Pablo', callTime: '07:00', actualTime: null, arrivalVarianceMinutes: null },
        { characterId: '3', characterName: 'DONNA NEURING', actorName: 'Annabelle Dexter-Jones', callTime: '09:00', actualTime: null, arrivalVarianceMinutes: null },
      ],
      punctualitySummary: { onTimeCount: 0, lateCount: 0, avgVarianceMinutes: null },
      firstLocation: { name: 'NEURING COMPOUND', status: 'INT' },
      weather: { forecast: 'Partly Cloudy', temp: 'High 68 / Low 42' },
      lastUpdatedBy: { documentType: 'call_sheet', label: 'Original Schedule', reportedOn: null, updatedAt: null },
    },
    {
      dayNumber: 10,
      status: 'planned',
      statusColor: '#A0AEC0',
      scenes: ['33', '34', '38', '39', 'VFX2', 'VFX3'],
      scenesPlanned: ['33', '34', '38', '39', 'VFX2', 'VFX3'],
      scenesShot: [],
      plannedScenesCount: 6,
      shotScenesCount: 0,
      plannedPages: '7 7/8',
      actualPages: '0/8',
      plannedPagesNumeric: 7.875,
      actualPagesNumeric: 0,
      progressPercent: 0,
      crewCallTime: '07:00',
      actualFirstShotTime: 'N/A',
      characters: [
        { characterId: '1', characterName: 'LAURA KALE', actorName: 'Cote de Pablo', callTime: '07:00', actualTime: null, arrivalVarianceMinutes: null },
        { characterId: '5', characterName: 'MARGOT BISHOP', actorName: 'Ali Larter', callTime: '08:00', actualTime: null, arrivalVarianceMinutes: null },
      ],
      punctualitySummary: { onTimeCount: 0, lateCount: 0, avgVarianceMinutes: null },
      firstLocation: { name: 'WAREHOUSE', status: 'INT' },
      weather: { forecast: 'Sunny', temp: 'High 74 / Low 48' },
      lastUpdatedBy: { documentType: 'call_sheet', label: 'Original Schedule', reportedOn: null, updatedAt: null },
    },
    {
      dayNumber: 11,
      status: 'planned',
      statusColor: '#A0AEC0',
      scenes: ['20', '24A', '24B', '32', '42A', '45', '47Apt'],
      scenesPlanned: ['20', '24A', '24B', '32', '42A', '45', '47Apt'],
      scenesShot: [],
      plannedScenesCount: 7,
      shotScenesCount: 0,
      plannedPages: '5 6/8',
      actualPages: '0/8',
      plannedPagesNumeric: 5.75,
      actualPagesNumeric: 0,
      progressPercent: 0,
      crewCallTime: '07:00',
      actualFirstShotTime: 'N/A',
      characters: [
        { characterId: '1', characterName: 'LAURA KALE', actorName: 'Cote de Pablo', callTime: '07:00', actualTime: null, arrivalVarianceMinutes: null },
        { characterId: '2', characterName: 'HAROLD KALE', actorName: 'Enrique Murciano', callTime: '08:00', actualTime: null, arrivalVarianceMinutes: null },
      ],
      punctualitySummary: { onTimeCount: 0, lateCount: 0, avgVarianceMinutes: null },
      firstLocation: { name: 'KALE RESIDENCE', status: 'INT' },
      weather: { forecast: 'Cloudy', temp: 'High 65 / Low 40' },
      lastUpdatedBy: { documentType: 'call_sheet', label: 'Original Schedule', reportedOn: null, updatedAt: null },
    },
    {
      dayNumber: 12,
      status: 'planned',
      statusColor: '#A0AEC0',
      scenes: ['10pt', '2pt', '61'],
      scenesPlanned: ['10pt', '2pt', '61'],
      scenesShot: [],
      plannedScenesCount: 3,
      shotScenesCount: 0,
      plannedPages: '5 6/8',
      actualPages: '0/8',
      plannedPagesNumeric: 5.75,
      actualPagesNumeric: 0,
      progressPercent: 0,
      crewCallTime: '07:00',
      actualFirstShotTime: 'N/A',
      characters: [
        { characterId: '1', characterName: 'LAURA KALE', actorName: 'Cote de Pablo', callTime: '07:00', actualTime: null, arrivalVarianceMinutes: null },
        { characterId: '7', characterName: 'ETHAN KALE', actorName: 'JR Bourne', callTime: '07:00', actualTime: null, arrivalVarianceMinutes: null },
      ],
      punctualitySummary: { onTimeCount: 0, lateCount: 0, avgVarianceMinutes: null },
      firstLocation: { name: 'AIRPORT', status: 'INT' },
      weather: { forecast: 'Clear', temp: 'High 72 / Low 46' },
      lastUpdatedBy: { documentType: 'call_sheet', label: 'Original Schedule', reportedOn: null, updatedAt: null },
    },
  ],
};

/**
 * Day 0 Demo Scene Analysis Data
 * Shows original schedule scene data per day (all planned, nothing shot)
 */
export const demoDay0SceneAnalysisData = {
  day1: {
    plannedScenes: ['4', '51', '52', '58', '60pt'],
    actualScenes: [],
    plannedPages: 4.25,
    actualPages: 0,
  },
  day2: {
    plannedScenes: ['14', '32A', '5A', '7A', '7B'],
    actualScenes: [],
    plannedPages: 4.625,
    actualPages: 0,
  },
  // ... continues for all 12 days
};

/**
 * Day 0 Demo Drift Analysis Data
 * Empty array - no drift data before production starts
 */
export const demoDay0DriftAnalysisData: any[] = [];

/**
 * Day 0 Demo Production Assumptions Data
 * Shows original 12-day schedule with no progress yet
 */
export const demoDay0ProductionAssumptionsData: ProductionAssumptionsData = {
  productionTitle: 'PROTOTYPE',
  baselineVersion: 'Original (White)',
  metrics: {
    schedule: {
      name: 'Schedule (Days)',
      original: 12,
      current: 0,
      variance: -12,
    },
    budget: {
      name: 'Budget (USD)',
      original: 4540290.0,
      current: 0,
      variance: -4540290.0,
    },
    creative: {
      name: 'Creative (Pages)',
      original: 62.875,
      current: 0,
      variance: -62.875,
    },
  },
};
